import { CopyOutlined, KeyOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Input,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import PageTitle from 'apps/home/components/PageTitle';
import { ENV_NAME, INVESTCORE_API_BASE_URL } from 'config/env-config';
import { createAccessTokenApiCall, type AccessToken } from './api';
import {
  buildMcpConfig,
  mcpUrl,
  normalizeBaseUrl,
  type McpClient,
} from './config';
import catalog from './tools.json';

type Access = 'read' | 'write' | 'destructive';

interface ToolRow {
  name: string;
  description: string;
  access: Access;
}

const CLIENT_OPTIONS: { value: McpClient; label: string }[] = [
  { value: 'claude-code-url-cli', label: 'Claude Code (URL)' },
  { value: 'claude-code-url', label: 'Claude Code (URL, .mcp.json)' },
  { value: 'claude-code', label: 'Local checkout (.mcp.json)' },
  { value: 'claude-code-cli', label: 'Local checkout (CLI)' },
  { value: 'claude-desktop', label: 'Claude Desktop (local)' },
];

const CLIENT_HINTS: Record<McpClient, string> = {
  'claude-code-url-cli':
    'Connects Claude Code to the hosted server; nothing to install. Run it anywhere: the token is saved to your local Claude Code config only.',
  'claude-code-url':
    'Save as .mcp.json in a project and export STAG_ADMIN_TOKEN in your shell (see above); Claude Code expands it in the Authorization header. The token is never written into .mcp.json.',
  'claude-code':
    'Save as .mcp.json at the stag repo root and export STAG_ADMIN_TOKEN in your shell (see above); Claude Code expands it when it starts the server. The token is never written into .mcp.json, since that file lives in the repo.',
  'claude-code-cli':
    'Run from the stag repo root. It saves the server, token included, to your local Claude Code config only.',
  'claude-desktop':
    'Add to claude_desktop_config.json (Settings → Developer → Edit Config) and set the absolute path to your stag checkout.',
};

const TOKEN_DAYS = [7, 30, 90];

const ACCESS_TAGS: Record<Access, { color: string; label: string }> = {
  read: { color: 'blue', label: 'read-only' },
  write: { color: 'green', label: 'write' },
  destructive: { color: 'red', label: 'destructive' },
};

const groups = catalog.groups.map(group => ({
  title: group.title,
  tools: group.tools.map(
    (tool): ToolRow => ({
      name: tool.name,
      description: tool.description,
      access: ((tool as { access?: string }).access ?? group.access) as Access,
    })
  ),
}));

const columns = [
  {
    title: 'Tool',
    dataIndex: 'name',
    width: 320,
    render: (name: string) => <Typography.Text code>{name}</Typography.Text>,
  },
  { title: 'Description', dataIndex: 'description' },
  {
    title: 'Access',
    dataIndex: 'access',
    width: 120,
    render: (access: Access) => (
      <Tag color={ACCESS_TAGS[access].color}>{ACCESS_TAGS[access].label}</Tag>
    ),
  },
];

const defaultBaseUrl = () =>
  normalizeBaseUrl(INVESTCORE_API_BASE_URL || window.location.origin);

export const OpsMcpConfigPage = () => {
  const [client, setClient] = useState<McpClient>('claude-code-url-cli');
  const [baseUrl, setBaseUrl] = useState(defaultBaseUrl);
  const [readOnly, setReadOnly] = useState(false);
  const [days, setDays] = useState(30);
  const [issued, setIssued] = useState<AccessToken | null>(null);
  const token = issued?.access_token ?? '';

  const generate = useMutation({
    mutationFn: () => createAccessTokenApiCall.queryFn({ days }),
    onSuccess: res => setIssued(res?.data ?? null),
  });

  const config = useMemo(
    () => buildMcpConfig({ client, baseUrl, token, readOnly }),
    [client, baseUrl, token, readOnly]
  );

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Copied');
    } catch {
      message.error('Could not copy; select the text instead');
    }
  };

  const visibleGroups = readOnly
    ? groups.map(g => ({ ...g, tools: g.tools.filter(t => t.access === 'read') }))
    : groups;

  return (
    <div>
      <PageTitle
        title="Ops MCP"
        backUrl="/admin"
        subTitle="Connect Claude Code or Claude Desktop to admin CP data and investment themes"
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="How it works"
          description={
            <>
              The <Typography.Text code>stag-ops</Typography.Text> MCP server
              is hosted at <Typography.Text code>/mcp</Typography.Text> on this
              API: add it to Claude Code by URL with your personal admin token.
              It calls the admin API as you, with the same access checks and
              validation as admin CP; every call is recorded in the{' '}
              <Link to="/agent/activity">agent activity log</Link>. Investment
              data is read-only; investment themes can be created and edited,
              but every change waits for your approval at{' '}
              <Link to="/agent/approvals">Stag Agent → Approvals</Link>.
              To chat with the same tools in the browser, use{' '}
              <Link to="/agent">Stag Agent</Link>. The local options run{' '}
              <Typography.Text code>apps/ops-mcp</Typography.Text> from a stag
              checkout instead (build it once with{' '}
              <Typography.Text code>pnpm --filter @stag/ops-mcp build</Typography.Text>
              ).
            </>
          }
        />

        {ENV_NAME === 'production' && !readOnly && (
          <Alert
            type="warning"
            showIcon
            message="This config points at production. Theme changes made by the agent go live once published. Turn on read-only for exploration."
          />
        )}

        <Card
          title={
            <Space>
              <KeyOutlined />
              Admin token
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              The MCP server signs in with a personal admin token. The token
              acts as you until it expires, or until your staff account is
              deactivated. It is shown only once: keep it in your shell or
              local client config, never in a committed file.
            </Typography.Paragraph>
            <Space wrap>
              <Typography.Text type="secondary">Valid for</Typography.Text>
              <Select
                value={days}
                onChange={setDays}
                style={{ width: 120 }}
                options={TOKEN_DAYS.map(d => ({ value: d, label: `${d} days` }))}
              />
              <Button
                type="primary"
                loading={generate.isPending}
                onClick={() => generate.mutate()}
              >
                {issued ? 'Generate a new token' : 'Generate token'}
              </Button>
            </Space>
            {issued && (
              <>
                <Alert
                  type="success"
                  showIcon
                  message={`Token for ${issued.email}, valid until ${new Date(
                    issued.expires_at
                  ).toLocaleString()}. Copy it now; it is not stored.`}
                />
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    readOnly
                    value={token}
                    data-testid="admin-token"
                    style={{ fontFamily: 'monospace' }}
                  />
                  <Button icon={<CopyOutlined />} onClick={() => copy(token)}>
                    Copy
                  </Button>
                </Space.Compact>
                {(client === 'claude-code' || client === 'claude-code-url') && (
                  <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    For .mcp.json, add to your shell profile:{' '}
                    <Typography.Text code copyable={{ text: `export STAG_ADMIN_TOKEN='${token}'` }}>
                      export STAG_ADMIN_TOKEN=…
                    </Typography.Text>
                  </Typography.Paragraph>
                )}
              </>
            )}
          </Space>
        </Card>

        <Card title="Configuration">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Segmented
              options={CLIENT_OPTIONS}
              value={client}
              onChange={value => setClient(value as McpClient)}
            />
            <Space wrap size="large">
              <Space direction="vertical" size={4}>
                <Typography.Text type="secondary">API base URL</Typography.Text>
                <Input
                  style={{ width: 320 }}
                  value={baseUrl}
                  onChange={e => setBaseUrl(e.target.value)}
                />
              </Space>
              <Space direction="vertical" size={4}>
                <Typography.Text type="secondary">Read-only</Typography.Text>
                <Switch checked={readOnly} onChange={setReadOnly} />
              </Space>
            </Space>

            {client.startsWith('claude-code-url') && (
              <Space size={4} wrap>
                <Typography.Text type="secondary">MCP URL</Typography.Text>
                <Typography.Text code copyable data-testid="mcp-url">
                  {mcpUrl(baseUrl, readOnly)}
                </Typography.Text>
              </Space>
            )}

            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {CLIENT_HINTS[client]}
            </Typography.Paragraph>

            <div style={{ position: 'relative' }}>
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copy(config)}
                style={{ position: 'absolute', top: 8, right: 8 }}
              >
                Copy
              </Button>
              <pre
                data-testid="mcp-config"
                style={{
                  background: '#0b1020',
                  color: '#e5e7eb',
                  padding: 16,
                  borderRadius: 8,
                  overflowX: 'auto',
                  margin: 0,
                  fontSize: 13,
                }}
              >
                {config}
              </pre>
            </div>
          </Space>
        </Card>

        {visibleGroups.map(group => (
          <Card
            key={group.title}
            title={`${group.title} (${group.tools.length})`}
          >
            <Table
              rowKey="name"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={group.tools}
            />
          </Card>
        ))}
      </Space>
    </div>
  );
};
